import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { ProviderOperationIdApiArg } from '~redux/modules/tic/api-slice/GENERATED_ARTIFACT';
import { ticApi } from '~redux/modules/tic/api-slice/tic.apiSlice';
import { getCurrentAccountId } from '~redux/selectors/accounts';
import type { ThunkDispatch } from '~types/redux';
import { getIntegrationJobStatus } from '../redux/tic.integrations.selectors';

const AUTO_RUN_SIGNATURE_TTL_MS = 10_000;
const autoRunSignatureStartTimes = new Map<string, number>();

function canStartAutoRun(signature: string): boolean {
  const now = Date.now();
  const lastStartedAt = autoRunSignatureStartTimes.get(signature);
  if (lastStartedAt && now - lastStartedAt < AUTO_RUN_SIGNATURE_TTL_MS) return false;
  autoRunSignatureStartTimes.set(signature, now);
  return true;
}

export interface UseIntegrationJobParams {
  providerId: ProviderOperationIdApiArg['provider'];
  connectionId: string | undefined;
  operationId: ProviderOperationIdApiArg['operationId'];
  /**
   * When true, a `synchronize` run clears TIC-side connection and subscription metadata before
   * execution so the provider reimports from a clean import cursor.
   */
  forceFullImport?: ProviderOperationIdApiArg['forceFullImport'];
  /**
   * When true, a successful `synchronize` run will finalize setup server-side (idempotent)
   * and enqueue the initial full Timely diff generation.
   */
  generateDiffAfterSync?: ProviderOperationIdApiArg['generateDiffAfterSync'];
  /** Specific job ID to track (optional - uses current job if not provided) */
  jobId?: string;
  /** Auto-run job on mount */
  autoRun?: boolean;
  /** Skip running the job (separate from autoRun for immediate loading control) */
  skip?: boolean;
  /** Callback when the job is successfully queued (job_id is known). */
  onQueued?: (jobId: string) => void;
  /** Callback when job completes successfully */
  onSuccess?: (data: { executed_operation_ids: string[]; message?: string }) => void;
  /** Callback when job fails */
  onError?: (error: { message: string; error?: string }) => void;
  /** Execution mode override */
  executionMode?: ProviderOperationIdApiArg['executionMode'];
}

/**
 * Hook for running integration jobs with Redux-based tracking.
 *
 * Features:
 * - Uses Redux selectors for state instead of local state
 * - Separates execution from status tracking (no direct Pusher subscription)
 * - Can track specific jobs by job ID or current job by context
 * - Status updates come from background Pusher manager -> Redux updates
 * - Job ID-based tracking provides perfect job isolation
 */
export function useIntegrationJob({
  providerId,
  connectionId,
  operationId,
  forceFullImport,
  generateDiffAfterSync,
  jobId,
  autoRun = false,
  skip = false,
  onQueued,
  onSuccess,
  onError,
  executionMode = 'worker',
}: UseIntegrationJobParams) {
  const dispatch = useDispatch<ThunkDispatch>();
  const [trackedJobId, setTrackedJobId] = useState<string | undefined>(jobId);
  const [startError, setStartError] = useState<string | null>(null);
  const [isStarting, setIsStarting] = useState(false);
  const hasAttemptedAutoRunRef = useRef(false);
  const handledTerminalStatusByJobIdRef = useRef<Map<string, 'completed' | 'failed'>>(new Map());

  // Get current Timely account ID for proper Pusher routing
  const currentTimelyAccountId = useSelector(getCurrentAccountId);

  // Always track by specific job ID - either provided or generated from execution
  const job = useSelector(trackedJobId ? getIntegrationJobStatus(trackedJobId) : () => undefined);

  // Execute job via RTKQ directly with memoization
  const executeJob = useCallback(
    async (modeOverride?: ProviderOperationIdApiArg['executionMode']) => {
      // Don't execute if connectionId is not available
      if (!connectionId) {
        console.warn('Cannot execute integration job without connectionId');
        setStartError('Missing connection');
        return;
      }

      const finalExecutionMode = modeOverride ?? executionMode;

      try {
        setStartError(null);
        setIsStarting(true);
        // Avoid showing stale job state while the new request is in-flight.
        if (!jobId) setTrackedJobId(undefined);

        const operationResult = await dispatch(
          ticApi.endpoints.providerOperationId.initiate({
            provider: providerId,
            connectionId,
            operationId,
            currentTimelyAccountId,
            executionMode: finalExecutionMode,
            ...(forceFullImport !== undefined ? { forceFullImport } : {}),
            ...(generateDiffAfterSync !== undefined ? { generateDiffAfterSync } : {}),
          })
        ).unwrap();

        if (!operationResult.job_id) {
          throw new Error('Failed to start integration job (missing job id)');
        }

        // Set the job ID from the job result for tracking
        setTrackedJobId(operationResult.job_id);
        onQueued?.(operationResult.job_id);
      } catch (err: any) {
        const message =
          err?.data?.message ||
          err?.error ||
          err?.message ||
          'Failed to start integration job. Please try again.';

        setStartError(message);
        onError?.({ message });

        // Error handling is also done by RTKQ enhancement + Redux, but we must
        // avoid the “optimistic running forever” state when job_id was never returned.
        console.error('Failed to execute integration job:', err);
      } finally {
        setIsStarting(false);
      }
    },
    [
      dispatch,
      providerId,
      connectionId,
      operationId,
      currentTimelyAccountId,
      executionMode,
      forceFullImport,
      generateDiffAfterSync,
      jobId,
      onQueued,
      onError,
    ]
  );

  // Auto-run on mount if enabled and not skipped.
  // React StrictMode can mount/unmount twice in dev; we need a guard that survives the dev remount.
  useEffect(() => {
    if (!autoRun || skip) return;
    if (!connectionId) return;

    const signature = [
      providerId,
      connectionId,
      operationId,
      executionMode,
      String(forceFullImport ?? ''),
      String(generateDiffAfterSync ?? ''),
    ].join('|');

    if (hasAttemptedAutoRunRef.current) return;
    if (!canStartAutoRun(signature)) return;

    hasAttemptedAutoRunRef.current = true;
    executeJob();
  }, [
    autoRun,
    skip,
    providerId,
    connectionId,
    operationId,
    executionMode,
    forceFullImport,
    generateDiffAfterSync,
    currentTimelyAccountId,
    executeJob,
  ]);

  // Effect to handle status changes and trigger callbacks
  useEffect(() => {
    if (!job) return;
    if (!trackedJobId) return;

    if (job.status === 'completed') {
      const previous = handledTerminalStatusByJobIdRef.current.get(trackedJobId);
      if (previous !== 'completed') {
        handledTerminalStatusByJobIdRef.current.set(trackedJobId, 'completed');
        onSuccess?.({
          executed_operation_ids: job.executed_operation_ids || [],
          message: job.message,
        });
      }
    }

    if (job.status === 'failed') {
      const previous = handledTerminalStatusByJobIdRef.current.get(trackedJobId);
      if (previous !== 'failed') {
        handledTerminalStatusByJobIdRef.current.set(trackedJobId, 'failed');
        onError?.({
          message: job.message || 'Integration job failed',
          error: job.error,
        });
      }
    }
  }, [job, trackedJobId, onSuccess, onError]);

  const isTrackedJobRunning = Boolean(trackedJobId) && (!job?.status || job?.status === 'started');

  return {
    /** Whether job is currently running (optimistic loading if autoRun + actual job status) */
    isRunning: isStarting || isTrackedJobRunning,

    /** Whether job completed successfully */
    isSuccess: job?.status === 'completed',

    /** Error message if job failed */
    error: startError ?? job?.error,

    /** Has any error occurred */
    hasError: Boolean(startError) || job?.status === 'failed',

    /** Function to manually trigger job */
    execute: executeJob,

    /** Current job ID for tracking */
    jobId: trackedJobId,

    /** Full job object for debugging */
    job,

    /** Reset is not needed - Redux manages state persistence */
    reset: () => {
      console.warn('Reset not implemented - Redux manages job state persistence');
    },
  };
}
